import React, { useState } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import ShadButton, {
  UNAVAILABLE_INK,
  UNAVAILABLE_SURFACE,
  buttonVariants,
} from './primitives/button';
import cn from './primitives/cn';
import { TooltipContent, TooltipProvider } from './primitives/tooltip';

/**
 * The CMS button.
 *
 * The API has not changed: `variant`, `size`, `pullRight`, `icon`, `className`. 233 call
 * sites keep working untouched. What changed underneath is that the paint now comes from
 * `primitives/button` (shadcn on Radix Slot, Tailwind for styling) instead of from
 * the `.btn` rules in main.css.
 *
 * **The legacy `.btn` classes are still emitted, and that is not laziness.** Bootstrap
 * itself is gone; main.css defines the names now. Two rules in
 * main.css position React buttons from the outside and cannot be moved into this
 * component, because the container is somewhere else:
 *
 *     .action-buttons .btn.btn-primary   { float: left; margin-right: 10px !important }
 *     .action-buttons .btn.btn-default   { background: transparent; color: black }
 *
 * `.action-buttons` is still what the toolbars and module footers render. The row-action
 * menu no longer is: it is `RowActions`, a Radix menu with nothing in main.css. The legacy
 * classes stay as positioning hooks while Tailwind does the painting, and
 * `important: true` in tailwind.config.js is what guarantees the paint wins. When
 * `.action-buttons` itself is converted, the hook can go; until then removing it is a
 * silent layout break, not a tidy-up.
 *
 * `pull-right` is likewise a legacy float (Bootstrap 3's name, main.css's rule), still used
 * by 52 call sites.
 */
const VARIANT_CLASS = {
  toolbar: null, // plain `btn`
  primary: 'btn-primary',
  secondary: 'btn-default',
  success: 'btn-success',
  danger: 'btn-danger',
  link: null,
  ghost: null,
};

/** `variant` names in the CMS API, mapped to the primitive's. */
const SHAD_VARIANT = {
  toolbar: 'default',
  primary: 'primary',
  secondary: 'secondary',
  success: 'success',
  danger: 'danger',
  link: 'link',
  /** An icon on its own in a table cell, such as the tags icon on the resource lists. */
  ghost: 'ghost',
};

const SIZE_CLASS = { xs: 'btn-xs', sm: 'btn-sm' };
const SHAD_SIZE = { xs: 'xs', sm: 'sm', icon: 'icon' };

/** The variants with no surface of their own: off, they only lose their ink. */
const INK_ONLY = new Set(['link', 'ghost']);

/**
 * The tooltip a button that tracks a reason sits in: open on hover and on focus while there
 * is a reason, never while there is none. It is rendered whether or not there is a reason
 * now, so the button keeps its element when the reason clears. That happens mid-click on a
 * form whose last field commits on blur (the money fields on Dues Payment): the mousedown
 * on Save blurs the field, the form becomes ready, and a button swapped for a new element
 * there would lose the click. Always controlled, for the same reason.
 *
 * It brings its own provider: the app root has one (store/rootComponents/Theme), but a
 * button rendered outside the app, as the tests render the bar, would otherwise throw.
 */
const ReasonTooltip = ({ reason, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root
        open={Boolean(reason) && open}
        onOpenChange={setOpen}
        delayDuration={200}
      >
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        {reason ? (
          <TooltipPrimitive.Portal>
            <TooltipContent side="top">
              {reason}
              <TooltipPrimitive.Arrow className="tw-fill-ink" />
            </TooltipContent>
          </TooltipPrimitive.Portal>
        ) : null}
      </TooltipPrimitive.Root>
    </TooltipProvider>
  );
};

/**
 * `disabledReason` (optional, SURF-4889) is why a disabled button is off, and makes the
 * button say so: Ryan's rule for every Save is grey and off, with a tooltip saying what to
 * fill out, until it can save. A button given one while `disabled`:
 *
 *   - is `aria-disabled` instead of `disabled`, so it stays in the tab order and keeps its
 *     pointer events. A `disabled` button takes neither, and a tooltip on it never opens,
 *     from the pointer or the keyboard.
 *   - looks exactly like a disabled one (UNAVAILABLE_SURFACE is DISABLED_SURFACE's paint).
 *   - shows the reason in a tooltip on hover and on focus; the tooltip is the button's
 *     description while it is open, so a screen reader hears it with the name.
 *   - presses nothing: a click, Enter or Space runs `onDisabledClick` (optional) instead of
 *     `onClick`, and its default is prevented, so a submit button in a form cannot submit
 *     it, implicitly from a field's Enter either.
 *
 * Without a reason, `disabled` is the attribute, as it always was. A button whose reason
 * comes and goes passes `disabledReason=""` while it has none (saveButtonState does), so its
 * element stays the same throughout (ReasonTooltip).
 */
const Button = React.forwardRef(
  (
    {
      variant = 'toolbar',
      size,
      icon,
      pullRight = false,
      className,
      children,
      disabled,
      disabledReason,
      onDisabledClick,
      onClick,
      ...rest
    },
    ref
  ) => {
    // A reason given, even an empty one, keeps the tooltip around the button.
    const tracksReason = disabledReason !== undefined && disabledReason !== null;
    const unavailable = Boolean(disabled && disabledReason);
    const button = (
      <ShadButton
        ref={ref}
        variant={SHAD_VARIANT[variant] || 'default'}
        size={SHAD_SIZE[size] || 'default'}
        className={cn(
          // Positioning hooks for main.css. See the note above.
          'btn',
          VARIANT_CLASS[variant],
          SIZE_CLASS[size],
          pullRight && 'pull-right',
          className,
          unavailable && (INK_ONLY.has(variant) ? UNAVAILABLE_INK : UNAVAILABLE_SURFACE)
        )}
        {...rest}
        disabled={unavailable ? undefined : disabled}
        aria-disabled={unavailable || rest['aria-disabled'] || undefined}
        onClick={
          unavailable
            ? (event) => {
                event.preventDefault();
                if (onDisabledClick) onDisabledClick(event);
              }
            : onClick
        }
      >
        {icon && <i className={icon} />}
        {children}
      </ShadButton>
    );
    if (!tracksReason) return button;
    return <ReasonTooltip reason={unavailable ? disabledReason : ''}>{button}</ReasonTooltip>;
  }
);
Button.displayName = 'Button';

export { buttonVariants };
export default Button;
